import { useApi, fetchApiRef, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { useEffect, useState, useCallback, useRef } from 'react';

export interface OnboardingStatus {
  complete: boolean;
  step: string;
  docsAcked: string[];
  teamSlug?: string;
}

export interface OnboardingStateResult {
  isLoading: boolean;
  isComplete: boolean;
  currentStep: string;
  docsAcked: string[];
  teamSlug?: string;
  /** Advance the wizard step (persisted server-side in memory) */
  updateStep: (step: string) => Promise<void>;
  /** Mark a single document as acknowledged */
  ackDoc: (docId: string) => Promise<void>;
  /** Open a PR to join an existing team */
  joinTeam: (teamSlug: string) => Promise<{ prUrl?: string; alreadyMember?: boolean }>;
  /** Open a PR to create a new team (with calling user as first member) */
  createTeam: (data: { name: string; slug: string; description: string; lead: string }) => Promise<{ prUrl: string }>;
  /** Provision Gitea account, commit onboarding-complete annotation, then mark done */
  completeOnboarding: () => Promise<void>;
  /** Provision platform resources (Gitea org, ArgoCD project) for the user's team */
  provisionTeam: (teamSlug: string) => Promise<{
    gitea: { status: 'created' | 'exists' | 'error'; error?: string };
    argocd: { status: 'created' | 'exists' | 'error'; error?: string };
    annotationPatched: boolean;
    provisioned: boolean;
  }>;
}

export function useOnboardingState(): OnboardingStateResult {
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const discovery = useApi(discoveryApiRef);
  const baseUrlRef = useRef<string | undefined>(undefined);

  /** Resolves the backend base URL once and caches it. */
  const getBase = useCallback(async () => {
    if (!baseUrlRef.current) {
      baseUrlRef.current = await discovery.getBaseUrl('app');
    }
    return baseUrlRef.current;
  }, [discovery]);

  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<OnboardingStatus>({
    complete: false,
    step: 'welcome',
    docsAcked: [],
  });

  // Fetch the current wizard state from the backend on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Verify user is authenticated (not guest) before calling the API
        const identity = await identityApi.getBackstageIdentity();
        if (!identity || !identity.userEntityRef) {
          // Guest — skip loading, leave complete=false so OnboardingGate bypasses
          if (!cancelled) setIsLoading(false);
          return;
        }

        const base = await getBase();
        const res = await fetchApi.fetch(`${base}/api/onboarding/status`);
        if (!res.ok) throw new Error(`status fetch failed: ${res.status}`);
        const data = (await res.json()) as OnboardingStatus;
        if (!cancelled) setStatus(data);
      } catch {
        // Network error or unauthenticated — fail open (don't block app)
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchApi, identityApi, getBase]);

  const updateStep = useCallback(
    async (step: string) => {
      setStatus(s => ({ ...s, step }));
      const base = await getBase();
      await fetchApi.fetch(`${base}/api/onboarding/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step }),
      });
    },
    [fetchApi, getBase],
  );

  const ackDoc = useCallback(
    async (docId: string) => {
      const newAcked = status.docsAcked.includes(docId)
        ? status.docsAcked
        : [...status.docsAcked, docId];
      setStatus(s => ({ ...s, docsAcked: newAcked }));
      const base = await getBase();
      await fetchApi.fetch(`${base}/api/onboarding/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docsAcked: newAcked }),
      });
    },
    [fetchApi, status.docsAcked, getBase],
  );

  const joinTeam = useCallback(
    async (teamSlug: string) => {
      const base = await getBase();
      const res = await fetchApi.fetch(`${base}/api/onboarding/join-team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamSlug }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error((err as any).error ?? 'join-team failed');
      }
      const data = await res.json();
      setStatus(s => ({ ...s, teamSlug }));
      return data as { prUrl?: string; alreadyMember?: boolean };
    },
    [fetchApi, getBase],
  );

  const createTeam = useCallback(
    async (data: { name: string; slug: string; description: string; lead: string }) => {
      const base = await getBase();
      const res = await fetchApi.fetch(`${base}/api/onboarding/create-team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error((err as any).error ?? 'create-team failed');
      }
      const result = await res.json();
      setStatus(s => ({ ...s, teamSlug: data.slug }));
      return result as { prUrl: string };
    },
    [fetchApi, getBase],
  );

  const provisionTeam = useCallback(
    async (teamSlug: string) => {
      const base = await getBase();
      const res = await fetchApi.fetch(`${base}/api/onboarding/provision-team`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamSlug }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error((err as any).error ?? 'provision-team failed');
      }
      return res.json() as Promise<{
        gitea: { status: 'created' | 'exists' | 'error'; error?: string };
        argocd: { status: 'created' | 'exists' | 'error'; error?: string };
        annotationPatched: boolean;
        provisioned: boolean;
      }>;
    },
    [fetchApi, getBase],
  );

  const completeOnboarding = useCallback(async () => {
    const base = await getBase();
    // Step 1: provision tool accounts
    await fetchApi.fetch(`${base}/api/onboarding/provision-tools`, { method: 'POST' });

    // Step 2: flip the onboarding-complete annotation and mark done
    const res = await fetchApi.fetch(`${base}/api/onboarding/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ complete: true }),
    });
    if (!res.ok) throw new Error('Failed to complete onboarding');

    setStatus(s => ({ ...s, complete: true, step: 'done' }));
  }, [fetchApi, getBase]);

  return {
    isLoading,
    isComplete: status.complete,
    currentStep: status.step,
    docsAcked: status.docsAcked,
    teamSlug: status.teamSlug,
    updateStep,
    ackDoc,
    joinTeam,
    createTeam,
    completeOnboarding,
    provisionTeam,
  };
}
